// Stylised 2D-anime energy materials (original shader work; technique reference only, no external textures).
// Look: saturated colour bands with hard (posterised) edges, a thin white-hot core, wobbling flame edges,
// scrolling electric streaks, and spiky flares at origin/impact. Only the core exceeds 1.0, so bloom stays tight.
import * as THREE from 'three/webgpu';
import { abs, atan, float, fract, mix, select, sin, step, time, uniform, uv, vec2 } from 'three/tsl';

export interface EnergyPalette { core: THREE.Color; hot: THREE.Color; body: THREE.Color; rim: THREE.Color }
export const PAL_TEAL: EnergyPalette = { core: new THREE.Color(0xf4ffff), hot: new THREE.Color(0x8af4ff), body: new THREE.Color(0x2f86e0), rim: new THREE.Color(0x173c9e) };
export const PAL_GOLD: EnergyPalette = { core: new THREE.Color(0xfff7ea), hot: new THREE.Color(0xffd45e), body: new THREE.Color(0xe0862a), rim: new THREE.Color(0x8a3a12) };

/** Shared, mutable palette uniforms (lerped when Surge toggles). */
export class EnergyUniforms {
  core = uniform(PAL_TEAL.core.clone()); hot = uniform(PAL_TEAL.hot.clone());
  body = uniform(PAL_TEAL.body.clone()); rim = uniform(PAL_TEAL.rim.clone());
  intensity = uniform(1);
  /** 0..1 how "thick" the bands are (fade in/out) */
  open = uniform(1);
  set(p: EnergyPalette, k = 1): void {
    (this.core.value as THREE.Color).lerp(p.core, k); (this.hot.value as THREE.Color).lerp(p.hot, k);
    (this.body.value as THREE.Color).lerp(p.body, k); (this.rim.value as THREE.Color).lerp(p.rim, k);
  }
}

// TSL node values are loosely typed here; this file builds them functionally.
/* eslint-disable @typescript-eslint/no-explicit-any */
function bands(r: any, u: EnergyUniforms, streak: any): any {
  // r: 0 at centre -> 1 at the outer edge. Hard steps = cel look.
  const c0 = u.core.mul(1.6), c1 = u.hot, c2 = u.body, c3 = u.rim;
  const inCore = step(r, float(0.2)), inHot = step(r, float(0.48)), inBody = step(r, float(0.78));
  let col: any = mix(c3, c2, inBody);
  col = mix(col, c1.add(streak.mul(0.35)), inHot);
  col = mix(col, c0, inCore);
  return col.mul(u.intensity);
}

/** Beam ribbon. Geometry: plane, uv.x across (0..1), uv.y along the beam (0 at hands -> 1 at tip). */
export function beamMaterial(u: EnergyUniforms): THREE.MeshBasicNodeMaterial {
  const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, fog: false });
  const t = time;
  const along = uv().y, across = abs(uv().x.sub(0.5)).mul(2);
  // flame-edge wobble that travels outward, stronger toward the tip; tapered start at the hands
  const wob = sin(along.mul(37).sub(t.mul(22))).mul(0.07).add(sin(along.mul(91).add(t.mul(31))).mul(0.045)).add(sin(along.mul(13).sub(t.mul(9))).mul(0.05));
  const taper = along.mul(9).clamp(0, 1).mul(0.35).add(0.65);
  const r = across.div(taper.mul(u.open)).add(wob.mul(along.mul(0.6).add(0.4)));
  // scrolling electric streaks inside the hot band
  const streak = step(fract(along.mul(7).sub(t.mul(5.5)).add(sin(uv().x.mul(19).add(t.mul(7))).mul(0.12))), float(0.06));
  m.colorNode = bands(r, u, streak);
  m.opacityNode = step(r, float(1)).mul(select(r.greaterThan(0.78), float(0.82), float(1)));
  return m;
}

/** Spiky flare (origin lens / impact / charge). Geometry: plane, uv centred. */
export function flareMaterial(u: EnergyUniforms, spikes = 7, spin = 3): THREE.MeshBasicNodeMaterial {
  const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, fog: false });
  const p = uv().sub(vec2(0.5, 0.5)).mul(2);
  const a = atan(p.y, p.x);
  const rr = p.length();
  const s1 = abs(sin(a.mul(spikes).add(time.mul(spin)))).pow(7).mul(0.42);
  const s2 = abs(sin(a.mul(spikes + 4).sub(time.mul(spin * 1.7)))).pow(11).mul(0.28);
  const edge = s1.add(s2).add(0.34).mul(u.open);
  const r = rr.div(edge.max(0.001));
  const streak = step(fract(rr.mul(4).sub(time.mul(3))), float(0.08));
  m.colorNode = bands(r, u, streak);
  m.opacityNode = step(r, float(1)).mul(select(r.greaterThan(0.78), float(0.82), float(1)));
  return m;
}
